import { useEffect, useReducer } from 'react'
import { ICartDictionary, IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { generateCartId } from '../lib/cartIdGenerator'

const DEFAULT_UNIT_TYPE = {
  type: 'default',
  interval: 1,
  format: '#0',
}

export const actions = {
  SET_PRODUCT_UNIT_TYPES: 'product/unit-types/SET_PRODUCT_UNIT_TYPES',
  SET_CURRENT_UNIT_TYPE: 'product/unit-types/SET_CURRENT_UNIT_TYPE',
  SET_CUSTOM_UNIT_QUANTITY: 'product/unit-types/SET_CUSTOM_UNIT_QUANTITY',
}

export interface IProductUnitType {
  type: string
  interval: number
  format: string
}

interface IUnitTypeState {
  hasUnitTypes: boolean
  unitTypes: IProductUnitType[]
  unitType?: IProductUnitType
  customUnitQuantity: Record<string, number>
}

const initialState: IUnitTypeState = {
  hasUnitTypes: false,
  unitTypes: [],
  unitType: DEFAULT_UNIT_TYPE,
  customUnitQuantity: {},
}

interface ISetUnitTypesAction {
  type: typeof actions.SET_PRODUCT_UNIT_TYPES
  payload: {
    hasUnitTypes: boolean
    unitTypes: IProductUnitType[]
    unitType?: IProductUnitType
    customUnitQuantity: Record<string, number>
  }
}

interface ISetCurrentUnitTypeAction {
  type: typeof actions.SET_CURRENT_UNIT_TYPE
  payload: {
    unitType?: IProductUnitType
  }
}

interface ISetCustomUnitQuantityAction {
  type: typeof actions.SET_CUSTOM_UNIT_QUANTITY
  payload: {
    customUnitQuantity: Record<string, number>
  }
}

type ProductUnitTypeActions = ISetUnitTypesAction | ISetCurrentUnitTypeAction | ISetCustomUnitQuantityAction

const reducer = (state: IUnitTypeState, action: ProductUnitTypeActions): IUnitTypeState => {
  switch (action.type) {
    case actions.SET_PRODUCT_UNIT_TYPES:
      return {
        ...state,
        ...action.payload,
      }
    case actions.SET_CURRENT_UNIT_TYPE:
      return {
        ...state,
        unitType: (action as ISetCurrentUnitTypeAction).payload.unitType,
      }
    case actions.SET_CUSTOM_UNIT_QUANTITY:
      return {
        ...state,
        customUnitQuantity: (action as ISetCustomUnitQuantityAction).payload.customUnitQuantity,
      }
    default:
      return state
  }
}

export const useUnitTypes = (cart: ICartDictionary, product?: IProduct) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    if (!product || !cart) {
      return
    }

    const cartId = generateCartId(product)
    const productInCart = cart[cartId] ?? product
    const unitTypes = productInCart?.metadata?.unitTypes
    const unitType =
      unitTypes?.find((unitType: IProductUnitType) => unitType.type === productInCart?.sizeDescription) ||
      unitTypes?.[0] ||
      DEFAULT_UNIT_TYPE
    const customUnitQuantity =
      (productInCart?.quantity ?? 0) > 0
        ? { ...state.customUnitQuantity, [unitType?.type ?? '']: productInCart.quantity }
        : state.customUnitQuantity

    dispatch({
      type: actions.SET_PRODUCT_UNIT_TYPES,
      payload: {
        unitType,
        unitTypes,
        hasUnitTypes: Array.isArray(unitTypes) && unitTypes.length > 0,
        customUnitQuantity,
      },
    })
  }, [product, cart])

  const setCurrentUnitType = (currentUnitType: IProductUnitType): void => {
    dispatch({ type: actions.SET_CURRENT_UNIT_TYPE, payload: { unitType: currentUnitType } })
  }

  const setCustomUnitQuantity = (type: string, quantity: number): void => {
    const customUnitQuantity = { ...state.customUnitQuantity, [type]: quantity }
    dispatch({ type: actions.SET_CUSTOM_UNIT_QUANTITY, payload: { customUnitQuantity } })
  }

  return {
    ...state,
    setCurrentUnitType,
    setCustomUnitQuantity,
  }
}
